import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { BUSINESS } from "@/lib/business-info";

const TESTIMONIALS = [
    {
        quote:
            "This restaurant is a hidden gem! We tried the jerk shrimp and brown stew chicken, both dishes bursting with flavor. Despite being busy cooking several to-go orders, Michelle was very sweet and took the time to chat with us. The dining area is private and beautifully decorated. A must-visit spot in Poughkeepsie!",
        author: "Victoria Guglielmo",
        meta: "Google Review",
    },
    {
        quote:
            "The kindest woman, Michelle! She opened her restaurant to us on New Year's Day while they were closed and cooked us the best meal. My boyfriend and I are vegan and she was very accommodating. The food was delicious and her hospitality was very special and welcoming. The restaurant was very clean and comfortable. We will come back again.",
        author: "Angelic Lopez",
        meta: "Google Review",
    },
    {
        quote:
            "Incredibly great food nestled away here in Poughkeepsie. I've had Jamaican food all over and none of these spots can compete with Empire Fusion. The restaurant is spotless, the owner is very nice, and her food is out of this world. I got the oxtails, peas and rice, sweet plantain, and steamed veggies. Everything tasted amazing.",
        author: "Yardley Keshinover",
        meta: "Google Review",
    },
];

const Stars = ({ value = 5, size = 14 }) => (
    <div className="flex items-center gap-0.5" aria-label={`${value} star rating`}>
        {Array.from({ length: 5 }).map((_, i) => (
            <Star
                key={i}
                size={size}
                className={
                    i < Math.round(value)
                        ? "fill-gold text-gold"
                        : "text-cream/20"
                }
                strokeWidth={1.2}
            />
        ))}
    </div>
);

export default function SocialProof() {
    return (
        <section
            data-testid="social-proof-section"
            className="relative empire-section bg-charcoal"
            aria-label="Social Proof"
        >
            <div className="empire-container">
                {/* Top trust strip */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6 border-y border-cream/10 py-10 mb-20">
                    <div className="flex items-center gap-5">
                        <div className="font-serif text-5xl text-gold leading-none">
                            {BUSINESS.rating.stars}
                        </div>
                        <div>
                            <Stars value={BUSINESS.rating.stars} />
                            <p className="empire-eyebrow mt-2 !text-cream/55 !text-[10px]">
                                186+ {BUSINESS.rating.source} reviews
                            </p>
                        </div>
                    </div>
                    <div className="md:border-l md:border-cream/10 md:pl-10">
                        <p className="empire-eyebrow mb-2">Featured For</p>
                        <p className="font-serif text-2xl text-cream tracking-tight leading-tight">
                            Hudson Valley's most soulful<br className="hidden md:block"/> Caribbean kitchen
                        </p>
                    </div>
                    <div className="md:border-l md:border-cream/10 md:pl-10">
                        <p className="empire-eyebrow mb-2">Trusted By</p>
                        <p className="text-cream/70 font-light text-[15px] leading-relaxed">
                            Corporate teams, family celebrations, weddings and
                            rehearsal dinners, and private gatherings across the
                            Hudson Valley.
                        </p>
                    </div>
                </div>

                {/* Section header */}
                <div className="max-w-3xl">
                    <p className="empire-eyebrow">What Guests Are Saying</p>
                    <h2
                        data-testid="social-proof-heading"
                        className="mt-5 empire-subhead text-cream"
                    >
                        Soulful food. Familiar warmth.<br />
                        <span className="italic text-gold">Always remembered.</span>
                    </h2>
                </div>

                {/* Testimonials */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-cream/10">
                    {TESTIMONIALS.map((t, i) => (
                        <motion.figure
                            key={i}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.1,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`testimonial-${i}`}
                            className="relative bg-charcoal-800 p-8 md:p-10 flex flex-col gap-6"
                        >
                            <Stars value={5} />
                            <blockquote className="font-serif text-cream text-[17px] md:text-[19px] leading-[1.55] tracking-tight">
                                &ldquo;{t.quote}&rdquo;
                            </blockquote>
                            <figcaption className="mt-auto">
                                <div className="text-gold text-sm tracking-wide">
                                    {t.author}
                                </div>
                                <div className="text-cream/45 text-[11px] uppercase tracking-[0.22em] mt-1">
                                    {t.meta}
                                </div>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
}
